import { ProgressCompletionSchema } from './progress.js';
import { z } from 'zod';
import { CustomerPromise } from './promise.js';
/** Customer/account lifecycle status. */
export const CustomerStatus = z.enum(['active', 'inactive']);
/** Overall account-health band, derived from ticket-side signals (never stored). */
export const CustomerHealth = z.enum(['healthy', 'watch', 'at_risk', 'critical']);
/**
 * A customer/account that can be linked to tickets (epics, features, stories).
 * Distinct from the legacy free-text `Ticket.customerName` scalar.
 */
export const Customer = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    domain: z.string().nullable(),
    status: CustomerStatus,
    trial: z.boolean(), // trial account — excluded from dashboard customer totals
    onboarded: z.boolean(), // derived from active promises in onboarding; legacy stored flag otherwise
    prospect: z.boolean().optional(), // server-owned; only signing approval clears this
    onboardingState: z.enum(['prospect', 'draft', 'active', 'done']).optional(), // derived onboarding state, not account status
    ballHolders: z.array(z.object({ userId: z.string(), userName: z.string() })).optional(),
    onboarding: z.boolean(), // in the onboarding pipeline
    migration: z.boolean(), // in the legacy→Waypoint migration pipeline
    iraSynced: z.boolean(), // provisioned from the IRA customer-name sync
    notes: z.string().nullable(),
    externalId: z.string().nullable(),
    // Account context (Phase 2) — describes the account, not the risk driver.
    tier: z.string().nullable(),
    arr: z.number().nullable(),
    region: z.string().nullable(),
    csmUserId: z.string().nullable(),
    csmName: z.string().nullable(), // resolved on reads
    createdAt: z.string(),
    updatedAt: z.string(),
    // Counts, only present on list reads. `ticketCount`/`doneTicketCount`/
    // `progressPct` cover the tickets linked to this customer's PROMISES (what
    // the onboarding bar measures) — not every ticket that references the
    // customer, and never cancelled/rejected ones (see progress.ts).
    ticketCount: z.number().optional(),
    promiseCount: z.number().optional(),
    doneTicketCount: z.number().optional(), // promise tickets in a done state
    completion: ProgressCompletionSchema.optional(),
    progressPct: z.number().optional(), // 0-100, mean status weight of those tickets
    blockedTicketCount: z.number().optional(), // linked tickets flagged blocked
    blockerTicketCount: z.number().optional(), // tickets linked to this customer's migration blockers
});
export const CreateCustomerInput = z.object({
    name: z.string().min(1).max(200),
    email: z.string().email().optional(),
    domain: z.string().max(200).optional(),
    status: CustomerStatus.optional(),
    trial: z.boolean().optional(),
    onboarded: z.boolean().optional(),
    onboarding: z.boolean().optional(),
    migration: z.boolean().optional(),
    notes: z.string().optional(),
    externalId: z.string().optional(),
    tier: z.string().max(50).optional(),
    arr: z.number().min(0).optional(),
    region: z.string().max(50).optional(),
    csmUserId: z.string().optional(),
});
// Nullable variants so account-context fields can be CLEARED via edit.
export const UpdateCustomerInput = CreateCustomerInput.partial().extend({
    tier: z.string().max(50).nullable().optional(),
    arr: z.number().min(0).nullable().optional(),
    region: z.string().max(50).nullable().optional(),
    csmUserId: z.string().nullable().optional(),
});
/** A point of contact at a customer account (Phase 3). */
export const CustomerContact = z.object({
    id: z.string(),
    customerId: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    role: z.string().nullable(),
    isPrimary: z.boolean(),
    notes: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateCustomerContactInput = z.object({
    name: z.string().min(1).max(200),
    email: z.string().email().optional(),
    phone: z.string().max(50).optional(),
    role: z.string().max(100).optional(),
    isPrimary: z.boolean().optional(),
    notes: z.string().optional(),
});
export const UpdateCustomerContactInput = CreateCustomerContactInput.partial().extend({
    email: z.string().email().nullable().optional(),
    phone: z.string().max(50).nullable().optional(),
    role: z.string().max(100).nullable().optional(),
});
/**
 * Aggregate statistics for a single customer, computed server-side from the
 * customer's linked tickets and promises. All figures are TICKET-DERIVED (the
 * source of truth for value/risk), so they stay live as tickets change — the
 * account-level fields on Customer are context, not the driver.
 *
 * Counts are true totals (they do not subtract project-restricted tickets the
 * viewer can't open), consistent with the customer/promise list rollups.
 */
export const CustomerStats = z.object({
    // Volume
    totalTickets: z.number(),
    openTickets: z.number(),
    doneTickets: z.number(),
    blockedTickets: z.number(),
    featureRequests: z.number(), // open tickets of type feature_request
    openBugs: z.number(), // open internal_bug + customer_support tickets
    // Distributions (bucket -> count), for the overview charts
    byStatus: z.record(z.number()),
    byType: z.record(z.number()),
    byPriority: z.record(z.number()),
    bySeverity: z.record(z.number()),
    // Financial (dollars). "At risk" sums open tickets; "delivered" sums done.
    arrAtRisk: z.number(),
    ltvAtRisk: z.number(),
    tcvAtRisk: z.number(),
    valueDelivered: z.number(), // impactArr on done tickets
    // Effort still open
    openStoryPoints: z.number(),
    openEstimateHours: z.number(),
    // Risk (attrition rating 1–5, from open tickets)
    attritionAvg: z.number().nullable(),
    attritionMax: z.number().nullable(),
    attritionHighCount: z.number(), // open tickets rated >= 4
    // Aging / throughput
    oldestOpenAgeDays: z.number().nullable(),
    avgOpenAgeDays: z.number().nullable(),
    createdLast30: z.number(), // tickets created in the last 30 days
    resolvedLast30: z.number(), // tickets moved to done in the last 30 days
    // Promises
    promiseTotal: z.number(),
    promiseOpen: z.number(),
    promiseOverdue: z.number(),
    promiseMet: z.number(),
    promiseMissed: z.number(),
    promiseFulfillmentRate: z.number().nullable(), // met / (met + missed)
    // Derived health band (ticket-signal blend) + 0–100 score
    health: CustomerHealth,
    healthScore: z.number(),
});
/** Full customer profile payload: the customer, its stats, promises, contacts. */
export const CustomerProfile = z.object({
    customer: Customer,
    stats: CustomerStats,
    promises: z.array(CustomerPromise),
    contacts: z.array(CustomerContact),
});
/**
 * The Onboarding page's grouping, shared by the page (apps/web Customers.tsx)
 * and the onboarding metric (GET /api/v2/metrics/onboarding) so they cannot
 * disagree. Only pipeline customers (`onboarding`) appear; the main list is
 * signed customers not yet Done. Prospects remain separate until signing
 * approval; the collapsed "Onboarded" group contains signed, completed customers. `onboarded` must come from the live read (withOnboardingStatus).
 * The page's "Show cancelled promises" toggle only hides promise rows inside an
 * expanded customer; it never changes membership or these groups.
 */
export function onboardingPipeline(customers) {
    const items = customers.filter((c) => c.onboarding);
    return { items, prospects: items.filter((c) => c.prospect), active: items.filter((c) => !c.prospect && !c.onboarded), done: items.filter((c) => !c.prospect && c.onboarded) };
}
/** Signing approval is restricted to these authenticated identities, regardless of role. */
export function canApproveCustomerSigning(email) {
    return ['chris@tracxsystems.com', 'jay@tracxsystems.com'].includes(email?.trim().toLowerCase() ?? '');
}
//# sourceMappingURL=customer.js.map